/**
 * The push keyset cursor, per (account, device), over the SDK storage seam
 * (events ADR-012; sdk ADR-011).
 *
 * Without it the cursor lives only in the stream instance and every cold launch
 * subscribes with no `after`, so the backend replays the account log from the
 * beginning — the backlog grows without bound and already-seen notifications
 * come back unread. Scoped by account so a second tenant on the same browser
 * profile never inherits another's floor.
 *
 * A malformed stored value THROWS rather than silently resetting the floor:
 * this module is its only writer, so a bad value is a defect, not a state to
 * absorb (.claude/rules/no-fallbacks.md).
 */
import type { NotificationCursor } from '../types/notification-types.js';
import type { NotificationCursorStore } from './bridge-events.js';
import type { SDKStorage } from '../storage/types.js';
import { SDK_STORAGE_NAMESPACE } from '../storage/types.js';

export function notificationCursorKey(accountId: string, deviceId: string): string {
  return `${SDK_STORAGE_NAMESPACE}push-cursor:${accountId}:${deviceId}`;
}

export function createNotificationCursorStore(
  storage: SDKStorage,
  accountId: string,
  deviceId: string,
): NotificationCursorStore {
  const key = notificationCursorKey(accountId, deviceId);
  return {
    load(): NotificationCursor | null {
      const raw = storage.getItem(key);
      if (raw === null) return null;
      const parsed: unknown = JSON.parse(raw);
      if (
        typeof parsed !== 'object' ||
        parsed === null ||
        typeof (parsed as NotificationCursor).created_at !== 'string' ||
        typeof (parsed as NotificationCursor).id !== 'string'
      ) {
        throw new Error(`stored push cursor at ${key} is not a {created_at, id} pair`);
      }
      const { created_at, id } = parsed as NotificationCursor;
      return { created_at, id };
    },
    save(cursor: NotificationCursor): void {
      storage.setItem(key, JSON.stringify({ created_at: cursor.created_at, id: cursor.id }));
    },
  };
}
